import { Modal } from "@msys/ui";
import {
  Alert,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useOpenFile } from "../../commons/hooks/useOpenFile.js";
import { useDownloadQuotePdf } from "../quotes/hooks/useDownloadQuotePdf.js";
import { UseDownloadQuotePdfOptions_QuoteFragment } from "../quotes/hooks/useDownloadQuotePdfOptions.generated.js";
import { useQuotePdfGenerationOptions } from "./useQuotePdfGenerationOptions.js";

const NAME_MAX = 255;

export function GenerateQuotePdfModal({
  projectId,
  docId,
  doc,
  canSave,
  handleClose,
}: {
  projectId: string;
  docId: string;
  doc: UseDownloadQuotePdfOptions_QuoteFragment;
  canSave: boolean;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["Quote", "Global"]);
  const { openPdf } = useOpenFile();
  const { options, loading } = useQuotePdfGenerationOptions({ doc });
  const { handleDownload, handleSaveAndShare, loadingPdf } =
    useDownloadQuotePdf({ projectId, docId });

  const [selectedKey, setSelectedKey] = React.useState<string>("");
  const [save, setSave] = React.useState(false);
  // Null until the user types: the name follows the selected template's label
  // for as long as they leave it alone.
  const [editedName, setEditedName] = React.useState<string | null>(null);

  const selected =
    options.find(option => option.key === selectedKey) ?? options[0];
  const name = editedName ?? selected?.label ?? "";
  const trimmed = name.trim();
  const isNameValid = trimmed.length > 0 && trimmed.length <= NAME_MAX;

  const isBusy = Boolean(loadingPdf);
  const canSubmit = Boolean(selected) && (!save || isNameValid) && !isBusy;

  // Each group's label is rendered once, above its first option.
  const renderedGroups = new Set<string>();

  return (
    <Modal
      title={t("Generate PDF", { ns: "Quote" })}
      handleClose={handleClose}
      maxWidth="xs"
      isLoading={loading}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: isBusy },
        },
        {
          label: t("Generate", { ns: "Global" }),
          handleClick: async () => {
            if (!selected || !canSubmit) return;
            const url = save
              ? await handleSaveAndShare(selected.args, trimmed)
              : await handleDownload(selected.args);
            if (!url) return;
            handleClose();
            await openPdf(url, `Quote_${docId}`);
          },
          buttonProps: { variant: "contained", disabled: !canSubmit },
        },
      ]}
    >
      <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
        <TextField
          variant="outlined"
          select
          label={t("Template", { ns: "Quote" })}
          value={selected?.key ?? ""}
          onChange={event => {
            setSelectedKey(event.target.value);
            setEditedName(null);
          }}
          disabled={isBusy}
          fullWidth
          size="small"
        >
          {options.flatMap(option => {
            const items = [];
            if (!renderedGroups.has(option.group)) {
              renderedGroups.add(option.group);
              items.push(
                <ListSubheader key={`group-${option.group}`}>
                  {option.group}
                </ListSubheader>
              );
            }
            items.push(
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            );
            return items;
          })}
        </TextField>

        {canSave && (
          <FormControlLabel
            control={
              <Switch
                checked={save}
                onChange={event => setSave(event.target.checked)}
                disabled={isBusy}
              />
            }
            label={t("Save this document", { ns: "Quote" })}
          />
        )}

        {save && (
          <>
            <Alert severity="info">
              {t(
                "Saved documents are visible to every organisation with read access to this quote.",
                { ns: "Quote" }
              )}
            </Alert>
            <TextField
              variant="outlined"
              label={t("Document name", { ns: "Quote" })}
              value={name}
              onChange={event => setEditedName(event.target.value)}
              slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
              error={name.length > 0 && !isNameValid}
              disabled={isBusy}
              fullWidth
              size="small"
            />
          </>
        )}
      </Stack>
    </Modal>
  );
}
